/**
 * Share links: the scene, deflated and base64url-encoded into the URL fragment. Fragments never
 * reach a server, so sharing stays frontend-only and private.
 */
import { newScene, sanitizeScene, type Scene } from '../shared/scene';

export const SHARE_PARAM = 's';
export const MAX_SHARE_CHARS = 8000;

async function pipe(
  bytes: Uint8Array,
  stream: CompressionStream | DecompressionStream,
): Promise<Uint8Array> {
  const out = new Blob([bytes as BlobPart]).stream().pipeThrough(stream);
  return new Uint8Array(await new Response(out).arrayBuffer());
}

function toBase64Url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(text: string): Uint8Array {
  const b64 = text.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

export async function encodeScene(scene: Scene): Promise<string> {
  const json = new TextEncoder().encode(JSON.stringify(scene));
  return toBase64Url(await pipe(json, new CompressionStream('deflate-raw')));
}

export async function decodeScene(encoded: string): Promise<Scene | null> {
  try {
    if (encoded.length > MAX_SHARE_CHARS * 2) return null;
    const bytes = await pipe(fromBase64Url(encoded), new DecompressionStream('deflate-raw'));
    if (bytes.length > 256_000) return null;
    const fallback = newScene();
    const scene = sanitizeScene(JSON.parse(new TextDecoder().decode(bytes)), fallback);
    return scene === fallback ? null : scene;
  } catch {
    return null;
  }
}

/** A full share URL for the current page. */
export async function shareUrl(
  scene: Scene,
  base: string,
): Promise<{ url: string; tooLong: boolean }> {
  const encoded = await encodeScene(scene);
  const url = `${base.split('#')[0]}#${SHARE_PARAM}=${encoded}`;
  return { url, tooLong: encoded.length > MAX_SHARE_CHARS };
}

/** The encoded scene in a location hash, if any. */
export function sharedSceneParam(hash: string): string | null {
  const m = new RegExp(`[#&]${SHARE_PARAM}=([A-Za-z0-9_-]+)`).exec(hash);
  return m ? m[1]! : null;
}
